import { SECURITY_SCOPED_DATA_POINTS } from '@dealstack/shared/lib/documents/fieldBindingCatalogue.ts';
import {
	IconBriefcase,
	IconBuilding,
	IconBuildingBank,
	IconBuildingCommunity,
	IconBuildingSkyscraper,
	IconCash,
	IconCertificate,
	IconClockPlay,
	IconCoins,
	IconDatabase,
	IconExchange,
	IconFlag,
	IconId,
	IconMapPin,
	IconReceiptTax,
	IconUser,
	IconUserCheck,
	IconUsersGroup,
} from '@tabler/icons-react';
import type { Icon } from '@tabler/icons-react';
import type {
	BindingMode,
	DataPointWithSampleRes,
	GroupedDataPoints,
	JoinWith,
	ListBinding,
	ListFieldSeparator,
	ListItemSeparator,
	SecurityScopedBinding,
} from './BindPanel.types.ts';

// Groups the flat catalogue by `DataPoint.group`, preserving first-seen group
// order (matches catalogue order rather than re-sorting alphabetically).
export const groupDataPoints = (catalogue: DataPointWithSampleRes[]): GroupedDataPoints[] => {
	const order: string[] = [];
	const byGroup = new Map<string, DataPointWithSampleRes[]>();
	catalogue.forEach((dataPoint) => {
		const existing = byGroup.get(dataPoint.group);
		if (existing) {
			existing.push(dataPoint);
		} else {
			byGroup.set(dataPoint.group, [dataPoint]);
			order.push(dataPoint.group);
		}
	});
	return order.map((group) => ({ group, dataPoints: byGroup.get(group) ?? [] }));
};

// Case-insensitive filter over label (and group, so searching "address"
// surfaces the whole "Registered address" group).
export const filterDataPoints = (
	catalogue: DataPointWithSampleRes[],
	search: string
): DataPointWithSampleRes[] => {
	const query = search.trim().toLowerCase();
	if (!query) {
		return catalogue;
	}
	return catalogue.filter(
		(dataPoint) =>
			dataPoint.label.toLowerCase().includes(query) ||
			dataPoint.group.toLowerCase().includes(query)
	);
};

// Separator text for each "Join with" option, applied both to the liquid
// expression built for the binding template and to the human-readable
// preview built from sample values.
const JOIN_SEPARATORS: Record<JoinWith, string> = {
	space: ' ',
	comma: ', ',
	newline: '\n',
	none: '',
};

// Preview text for the "THIS BLANK WILL RESOLVE TO" card: the selected data
// points' sample values joined by the chosen separator, in selection order —
// mirrors what `buildJoinedLiquidExpression` will render once the bound data
// is live.
export const previewFor = (
	selectedPaths: string[],
	catalogue: DataPointWithSampleRes[],
	joinWith: JoinWith = 'space'
): string => {
	const byPath = new Map(catalogue.map((dataPoint) => [dataPoint.path, dataPoint]));
	return selectedPaths
		.map((path) => byPath.get(path)?.sampleValue)
		.filter((value): value is string => value != null)
		.join(JOIN_SEPARATORS[joinWith]);
};

// Wraps `buildLiquidExpression` so combined selections can use a separator
// other than the library default's single space (Comma / New line / None).
// A single-path selection is unaffected by the separator either way, and the
// default keeps existing single-arg call sites (and their saved templates)
// working unchanged.
export const buildJoinedLiquidExpression = (
	paths: string[],
	joinWith: JoinWith = 'space'
): string => paths.map((path) => `{{ ${path} }}`).join(JOIN_SEPARATORS[joinWith]);

// Inverse of `buildJoinedLiquidExpression`/`buildLiquidExpression` (which
// wrap each path as `{{ path }}` and join with some separator): pulls the
// liquid paths back out of a saved binding template so a field's current
// selection can be restored when the panel is opened for it — the separator
// used to join them doesn't matter for this, only the paths do. Returns []
// for null/empty/no-match input.
export const parsePathsFromExpression = (template: string | null | undefined): string[] => {
	if (!template) {
		return [];
	}
	const matches = template.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g);
	return Array.from(matches, (match) => match[1].trim());
};

// Maps a catalogue group name to the Tabler icon shown in its list-section
// header. Falls back to a generic database icon for any group not in the
// known mapping (e.g. future groups added to FIELD_BINDING_CATALOGUE).
const GROUP_ICONS: Record<string, Icon> = {
	'Investor profile': IconUser,
	'Registered address': IconMapPin,
	'Investor KYC': IconId,
	'Tax residences': IconReceiptTax,
	Nationality: IconFlag,
	Employment: IconBriefcase,
	'Investor bank account': IconBuildingBank,
	Representative: IconUserCheck,
	Programme: IconBuildingCommunity,
	Company: IconBuilding,
	'Programme bank account': IconBuildingBank,
	'Investment vehicle': IconBuildingSkyscraper,
	'Holdings & securities': IconCoins,
	Loans: IconCash,
	Cohorts: IconUsersGroup,
	Security: IconCertificate,
	Holding: IconCoins,
	'Completed transaction': IconExchange,
	'Transaction in progress': IconClockPlay,
};

export const iconForGroup = (group: string): Icon => GROUP_ICONS[group] ?? IconDatabase;

// --- Repeating-list ("for each") binding templates -----------------------
// A list binding renders one segment per item of a Liquid list, so a single
// blank can print e.g. "1000 of Ordinary Shares and 500 of Preference Shares".
// The loop variable is always `item`; field paths are stored RELATIVE to it.
//
// Format (deterministic — exactly what parseListLiquidExpression inverts):
//   {% for item in <source> %}<f1>{fieldSep}<f2>…{% unless forloop.last %}<itemSep>{% endunless %}{% endfor %}
// where each <fN> is `{{ item.<relativePath> }}`.

// Prefix that marks a template as a list-loop binding. The loop variable name
// is fixed to `item`, so any template the UI produces starts with this.
const LIST_TEMPLATE_PREFIX = '{% for item in ';

// True when `template` looks like a list-loop binding this module produces.
export const isListExpression = (template: string | null | undefined): boolean =>
	typeof template === 'string' && template.startsWith(LIST_TEMPLATE_PREFIX);

// Builds the `{% for %}` loop template from a structured list binding.
// `itemPaths` are relative to the loop item (e.g. "unitsHeld"); each becomes
// `{{ item.<path> }}`, joined by `fieldSeparator`, with `itemSeparator` printed
// between items (via `{% unless forloop.last %}`).
export const buildListLiquidExpression = ({
	source,
	itemPaths,
	fieldSeparator,
	itemSeparator,
}: ListBinding): string => {
	const fields = itemPaths.map((path) => `{{ item.${path} }}`).join(fieldSeparator);
	return `{% for item in ${source} %}${fields}{% unless forloop.last %}${itemSeparator}{% endunless %}{% endfor %}`;
};

// Matches the whole loop, capturing source / body / itemSeparator.
const LIST_TEMPLATE_RE =
	/^\{%\s*for\s+item\s+in\s+(.+?)\s*%\}([\s\S]*?)\{%\s*unless\s+forloop\.last\s*%\}([\s\S]*?)\{%\s*endunless\s*%\}\{%\s*endfor\s*%\}$/;

// Pulls the `{{ item.<path> }}` field expressions out of the loop body, and
// the literal text (fieldSeparator) sitting between the first two of them.
const ITEM_FIELD_RE = /\{\{\s*item\.([^}]+?)\s*\}\}/g;

// Inverse of `buildListLiquidExpression`. Returns null when `template` is not a
// list-loop binding this module produces. Round-trips losslessly for the
// shapes the UI generates (single- and multi-field). `fieldSeparator` defaults
// to a single space when there is only one field (no separator to observe).
export const parseListLiquidExpression = (
	template: string | null | undefined
): ListBinding | null => {
	if (!template) {
		return null;
	}
	const match = template.match(LIST_TEMPLATE_RE);
	if (!match) {
		return null;
	}
	const source = match[1];
	const body = match[2];
	const itemSeparator = match[3];

	const fieldMatches = Array.from(body.matchAll(ITEM_FIELD_RE));
	if (fieldMatches.length === 0) {
		return null;
	}
	const itemPaths = fieldMatches.map((fieldMatch) => fieldMatch[1].trim());

	// fieldSeparator is the literal text between the end of the first field
	// expression and the start of the second. With a single field there is no
	// separator to observe, so default to a space (matches the UI default).
	let fieldSeparator = ' ';
	if (fieldMatches.length >= 2) {
		const firstEnd = (fieldMatches[0].index ?? 0) + fieldMatches[0][0].length;
		const secondStart = fieldMatches[1].index ?? firstEnd;
		fieldSeparator = body.slice(firstEnd, secondStart);
	}

	return { source, itemPaths, fieldSeparator, itemSeparator };
};

// The literal separator string each list preset renders into the loop.
export const LIST_FIELD_SEPARATORS: Record<ListFieldSeparator, string> = {
	space: ' ',
	of: ' of ',
	comma: ', ',
};

export const LIST_ITEM_SEPARATORS: Record<ListItemSeparator, string> = {
	and: ' and ',
	comma: ', ',
	newline: '\n',
};

// Best-effort inverse: map a stored literal separator back to its preset so an
// existing list template restores the right control selection. Falls back to
// the first preset when the literal isn't one we produce.
export const fieldSeparatorPreset = (literal: string): ListFieldSeparator => {
	const found = (Object.keys(LIST_FIELD_SEPARATORS) as ListFieldSeparator[]).find(
		(key) => LIST_FIELD_SEPARATORS[key] === literal
	);
	return found ?? 'space';
};

export const itemSeparatorPreset = (literal: string): ListItemSeparator => {
	const found = (Object.keys(LIST_ITEM_SEPARATORS) as ListItemSeparator[]).find(
		(key) => LIST_ITEM_SEPARATORS[key] === literal
	);
	return found ?? 'and';
};

// Typed narrowing for Mantine SegmentedControl `onChange` (which yields a bare
// string) — avoids `as` casts at the call sites.
export const isBindingMode = (value: string): value is BindingMode =>
	value === 'single' || value === 'security' || value === 'list';

export const isListFieldSeparator = (value: string): value is ListFieldSeparator =>
	value === 'space' || value === 'of' || value === 'comma';

export const isListItemSeparator = (value: string): value is ListItemSeparator =>
	value === 'and' || value === 'comma' || value === 'newline';

// --- Security-scoped binding templates -----------------------------------
// A scoped binding pins ONE security by id, so the same blank states the same
// security for every recipient — and renders empty for a recipient who has no
// position in it, which is the intended behaviour rather than a failure.
//
// Format (deterministic — exactly what parseScopedLiquidExpression inverts):
//   {{ data.<source> | where: "security.id", "<id>" | map: "<path>" | first <format> }}
// joined by the JoinWith separator. `format` MUST follow `first`: `map:` takes a
// property path only, so a filter inside it silently resolves to nothing.

const scopedFormatFor = (path: string): string =>
	SECURITY_SCOPED_DATA_POINTS.find((point) => point.path === path)?.format ?? '';

export const buildScopedLiquidExpression = ({
	securityId,
	source,
	paths,
	joinWith,
}: SecurityScopedBinding): string =>
	paths
		.map((path) => {
			const format = scopedFormatFor(path);
			const tail = format ? `| first ${format}` : '| first';
			return `{{ data.${source} | where: "security.id", "${securityId}" | map: "${path}" ${tail} }}`;
		})
		.join(JOIN_SEPARATORS[joinWith]);

// Captures source, securityId and the bare relative path from each expression.
// `[^}]*` swallows any trailing format chain without capturing it — the format
// is re-derived from the catalogue on rebuild, so it cannot desync.
const SCOPED_FIELD_RE =
	/\{\{\s*data\.(holdings|allocations)\s*\|\s*where:\s*"security\.id",\s*"([^"]*)"\s*\|\s*map:\s*"([^"]+)"\s*\|\s*first[^}]*\}\}/g;

// Best-effort inverse of JOIN_SEPARATORS, for restoring the control selection
// from a saved template. Falls back to 'space' for a literal we don't produce.
export const joinWithPreset = (literal: string): JoinWith => {
	const found = (Object.keys(JOIN_SEPARATORS) as JoinWith[]).find(
		(key) => JOIN_SEPARATORS[key] === literal
	);
	return found ?? 'space';
};

/**
 * Inverse of `buildScopedLiquidExpression`. Returns null when `template` is not
 * a scoped binding this module produces — including a template whose
 * expressions disagree on the security or the source, which no UI state could
 * have produced and which the panel must not silently normalise away.
 */
export const parseScopedLiquidExpression = (
	template: string | null | undefined
): SecurityScopedBinding | null => {
	if (!template) {
		return null;
	}
	const matches = Array.from(template.matchAll(SCOPED_FIELD_RE));
	if (matches.length === 0) {
		return null;
	}
	const [first] = matches;
	const source = first[1] === 'allocations' ? 'allocations' : 'holdings';
	const securityId = first[2];
	if (matches.some((match) => match[1] !== first[1] || match[2] !== securityId)) {
		return null;
	}

	// The separator is the literal text between the end of the first expression
	// and the start of the second. With a single expression there is none to
	// observe, so default to a space — mirrors parseListLiquidExpression.
	let joinWith: JoinWith = 'space';
	if (matches.length >= 2) {
		const firstEnd = (matches[0].index ?? 0) + matches[0][0].length;
		const secondStart = matches[1].index ?? firstEnd;
		joinWith = joinWithPreset(template.slice(firstEnd, secondStart));
	}

	return {
		securityId,
		source,
		paths: matches.map((match) => match[3]),
		joinWith,
	};
};
